/**
 * Theme Context for KineServe
 * Manages dark/light theme preference with localStorage persistence
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

const ThemeContext = createContext(undefined);

const THEME_STORAGE_KEY = 'kineserve-theme';

// Pages that should always show in light mode
const LIGHT_MODE_ONLY_PATHS = ['/login', '/verify-email', '/activate-account'];

export const ThemeProvider = ({ children }) => {
  // Initialize theme from localStorage, default to 'light'
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(THEME_STORAGE_KEY);
      return saved || 'light';
    }
    return 'light';
  });

  // Check if current path requires light mode
  const isLightModeOnlyPath = useCallback(() => {
    if (typeof window !== 'undefined') {
      const path = window.location.pathname;
      return LIGHT_MODE_ONLY_PATHS.some(p => path.startsWith(p));
    }
    return false;
  }, []);

  // Apply theme class to document root
  useEffect(() => {
    const root = document.documentElement;
    
    // If on a light-mode-only page, force light mode
    if (isLightModeOnlyPath()) {
      root.classList.remove('dark');
    } else if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    
    // Persist to localStorage (even if we're forcing light mode, remember user preference)
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  }, [theme, isLightModeOnlyPath]);

  // Re-apply theme when route changes
  useEffect(() => {
    const handleRouteChange = () => {
      const root = document.documentElement;
      if (isLightModeOnlyPath()) {
        root.classList.remove('dark');
      } else if (theme === 'dark') {
        root.classList.add('dark');
      }
    };

    // Listen for popstate (back/forward navigation)
    window.addEventListener('popstate', handleRouteChange);
    return () => window.removeEventListener('popstate', handleRouteChange);
  }, [theme, isLightModeOnlyPath]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  const setLightTheme = () => setTheme('light');
  const setDarkTheme = () => setTheme('dark');

  const value = {
    theme,
    isDark: theme === 'dark',
    toggleTheme,
    setLightTheme,
    setDarkTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;
